import React from 'react';
import {
  IconBus,
  IconUsersGroup,
  IconSchool,
  IconUserPlus,
  IconCash,
  IconCoins,
  IconCreditCard,
  IconReportAnalytics,
  IconCalendarEvent,
  IconPackage,
  IconAlertTriangle,
} from '@tabler/icons-react';

// Categories used to group the "add card" picker.
// NOTE: library is intentionally omitted for now - the library module is disabled
// for all accounts (Account.show_library defaults to False), so there's nothing to show.
export const CARD_CATEGORIES = {
  students: 'الطلاب',
  staff: 'الموظفون',
  classes: 'الصفوف',
  buses: 'الباصات',
  finance: 'المالية',
  finance_range: 'المالية (حسب فترة)',
  inventory: 'المستودع',
  events: 'الفعاليات',
};

// The 10 cards shown by default. Metadata only (title/category) for the picker -
// their actual values are computed in DashboardPage.js's statCards useMemo, keyed by these ids.
export const DEFAULT_DASHBOARD_CARDS = [
  { id: 'monthly_receipts', category: 'finance', title: 'إجمالي المقبوضات الشهرية' },
  { id: 'monthly_expenses', category: 'finance', title: 'إجمالي المصروفات الشهرية' },
  { id: 'students', category: 'students', title: 'الطلاب' },
  { id: 'staff', category: 'staff', title: 'الموظفون' },
  { id: 'bus_costs', category: 'buses', title: 'تكاليف الباصات' },
  { id: 'today_purchases', category: 'inventory', title: 'المشتريات اليوم' },
  { id: 'today_payments', category: 'finance', title: 'المدفوعات اليوم' },
  { id: 'profits', category: 'inventory', title: 'الأرباح' },
  { id: 'misc_items', category: 'inventory', title: 'المتفرقات' },
  { id: 'misc_remaining', category: 'inventory', title: 'المتفرقات المتبقية' },
];

// Default ids also include a couple of "optional-mechanism" cards (daily payment/receipt
// totals) that we want shown out of the box, alongside the always-visible monthly ones -
// their definitions stay in OPTIONAL_DASHBOARD_CARDS since that's what drives lazy-fetching.
export const DEFAULT_CARD_IDS = [
  ...DEFAULT_DASHBOARD_CARDS.map((c) => c.id),
  'daily_payments_today',
  'daily_receipts_today',
];

// Optional dashboard cards a user can add via the corner picker.
// `needsFetch: true` means this card's data is NOT already in the redux store
// and must be lazily fetched (via fetchOptionalCardData) only when enabled.
// Ids here MUST match school_BE/students/dashboard_cards.py OPTIONAL_DASHBOARD_CARD_IDS.
export const OPTIONAL_DASHBOARD_CARDS = [
  // ---- Tier 1: already in dashboardSummary / redux store, zero fetch cost ----
  { id: 'bus_utilization', category: 'buses', title: 'نسبة إشغال الباصات', icon: <IconBus size={32} />, color: '#FFB74D', needsFetch: false },
  { id: 'avg_students_per_class', category: 'classes', title: 'متوسط الطلاب لكل صف', icon: <IconUsersGroup size={32} />, color: '#2196F3', needsFetch: false },
  { id: 'class_distribution', category: 'classes', title: 'توزيع الصفوف', icon: <IconUsersGroup size={32} />, color: '#3F51B5', needsFetch: false },
  { id: 'employee_type_distribution', category: 'staff', title: 'توزيع أنواع الموظفين', icon: <IconSchool size={32} />, color: '#673AB7', needsFetch: false },
  { id: 'new_students_30d', category: 'students', title: 'طلاب جدد (٣٠ يوم)', icon: <IconUserPlus size={32} />, color: '#009688', needsFetch: false },
  { id: 'new_employees_30d', category: 'staff', title: 'موظفون جدد (٣٠ يوم)', icon: <IconUserPlus size={32} />, color: '#7E57C2', needsFetch: false },
  { id: 'collection_rate', category: 'finance', title: 'نسبة التحصيل', icon: <IconReportAnalytics size={32} />, color: '#43A047', needsFetch: false },
  { id: 'avg_payment_size', category: 'finance', title: 'متوسط قيمة الدفعة', icon: <IconCoins size={32} />, color: '#F9A825', needsFetch: false },
  { id: 'payment_methods_breakdown', category: 'finance', title: 'طرق الدفع الأكثر استخداماً', icon: <IconCreditCard size={32} />, color: '#1565C0', needsFetch: false },
  { id: 'total_outstanding', category: 'finance', title: 'إجمالي المستحقات غير المحصلة', icon: <IconAlertTriangle size={32} />, color: '#D32F2F', needsFetch: false },
  { id: 'remaining_salaries', category: 'staff', title: 'رواتب الموظفين المتبقية', icon: <IconCash size={32} />, color: '#F57C00', needsFetch: false },

  // ---- Tier 2: existing backend endpoint, lazily fetched on first enable ----
  { id: 'employee_dashboard_stats', category: 'staff', title: 'إحصائيات الرواتب (متوسط/أعلى/أدنى)', icon: <IconCash size={32} />, color: '#5E35B1', needsFetch: true },
  { id: 'payment_dashboard_stats', category: 'finance', title: 'ملخص المدفوعات والسندات المعلقة', icon: <IconCreditCard size={32} />, color: '#00838F', needsFetch: true },

  // ---- Tier 3: new lightweight endpoints, lazily fetched on first enable ----
  { id: 'daily_payments_today', category: 'finance', title: 'إجمالي المصروفات اليومية', icon: <IconCash size={32} />, color: '#E53935', needsFetch: true },
  { id: 'daily_receipts_today', category: 'finance', title: 'إجمالي المقبوضات اليومية', icon: <IconCash size={32} />, color: '#43A047', needsFetch: true },
  { id: 'today_absences', category: 'staff', title: 'غيابات اليوم', icon: <IconAlertTriangle size={32} />, color: '#FB8C00', needsFetch: true },
  { id: 'events_summary', category: 'events', title: 'الفعاليات القادمة', icon: <IconCalendarEvent size={32} />, color: '#5C6BC0', needsFetch: true },
  { id: 'total_inventory_value', category: 'inventory', title: 'القيمة الإجمالية للمخزون', icon: <IconPackage size={32} />, color: '#9C27B0', needsFetch: true },
  { id: 'low_stock_items', category: 'inventory', title: 'عناصر المخزون المقتربة على الانتهاء', icon: <IconAlertTriangle size={32} />, color: '#EF6C00', needsFetch: false },
  { id: 'bus_stats', category: 'buses', title: 'إحصائيات الحافلات', icon: <IconBus size={32} />, color: '#00897B', needsFetch: false },

  // ---- Tier 4: Finance Staff section - rendered in their own date-filterable section
  // (not the generic grid) and fetched together via fetchFinanceRangeStats, not
  // the per-card fetchOptionalCardData mechanism. See DashboardPage.js.
  // NOTE: payment_methods_breakdown_range is listed first so it renders at the top of
  // قسم المالية حسب التاريخ (DashboardPage.js sorts financeRangeCards by this array's order). ----
  { id: 'payment_methods_breakdown_range', category: 'finance_range', title: 'طرق الدفع الأكثر استخداماً', icon: <IconCreditCard size={32} />, color: '#1565C0', needsFetch: false, dateFilterable: true },
  { id: 'receipts_collected_range', category: 'finance_range', title: 'إجمالي المقبوضات', icon: <IconCash size={32} />, color: '#43A047', needsFetch: false, dateFilterable: true },
  { id: 'expenses_range', category: 'finance_range', title: 'إجمالي المصروفات', icon: <IconCash size={32} />, color: '#E53935', needsFetch: false, dateFilterable: true },
  { id: 'collection_rate_range', category: 'finance_range', title: 'نسبة التحصيل', icon: <IconReportAnalytics size={32} />, color: '#00838F', needsFetch: false, dateFilterable: true },
  { id: 'avg_payment_size_range', category: 'finance_range', title: 'متوسط قيمة الدفعة', icon: <IconCoins size={32} />, color: '#F9A825', needsFetch: false, dateFilterable: true },
  { id: 'salary_stats_range', category: 'finance_range', title: 'إحصائيات الرواتب (متوسط/أعلى/أدنى)', icon: <IconCash size={32} />, color: '#5E35B1', needsFetch: false, dateFilterable: true },
];

// The 3 pinned cards for قسم المالية حسب التاريخ (مجمل أقساط الطلاب / رواتب الموظفين /
// سندات قبض غير محصلة) are NOT defined here - they're the existing detailed pie-chart
// widgets in DashboardPage.js (Students Financial / Employee Salaries / Unpaid
// Transactions widgets), rewired to read from fetchFinanceRangeStats and relocated
// into that section instead of being duplicated as simple stat cards.

export const getOptionalCardDef = (id) => OPTIONAL_DASHBOARD_CARDS.find((c) => c.id === id);
